import React, { useState, useRef, useEffect } from 'react';
import { X, UploadCloud, FileText, CheckCircle2, RotateCcw, AlertCircle } from 'lucide-react';
import { createPortal } from 'react-dom';
import * as XLSX from 'xlsx-js-style';
import { supabase } from '../../../src/lib/supabase';
import { logUsageEvent } from '../../../src/lib/logger';
import { useAuth } from '../../../src/contexts/AuthContext';
import { useToast } from '../../../src/contexts/ToastContext';

interface UploadRateioModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

// Função extractDayFromValue removida por não ser mais necessária.

const SYSTEM_FIELDS = [
  { key: 'uc', label: 'UC', required: false },
  { key: 'outro_numero_uc', label: 'Outro N° UC', required: false },
  { key: 'status', label: 'Status', required: false },
  { key: 'cpf_cnpj', label: 'CPF/CNPJ', required: false },
  { key: 'nome', label: 'Nome', required: false },
  { key: 'uc_geradora', label: 'UC GERADORA', required: false },
  { key: 'percent_rateio', label: '% de rateio', required: false },
];

const UploadRateioModal: React.FC<UploadRateioModalProps> = ({ onClose, onSuccess }) => {
  const { profile } = useAuth();
  const { addToast } = useToast();

  const [file, setFile] = useState<File | null>(null);
  const [fileHeaders, setFileHeaders] = useState<string[]>([]);
  const [fileData, setFileData] = useState<any[]>([]);

  const [mesReferencia, setMesReferencia] = useState('');
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>({}); // SystemField -> FileHeader

  const [isProcessing, setIsProcessing] = useState(false);
  const [step, setStep] = useState<1 | 2>(1); // 1 = Upload, 2 = Mapping
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Auto-guess mapping when headers change
  useEffect(() => {
    if (fileHeaders.length > 0) {
      const initialMap: Record<string, string> = {};
      const normalizedHeaders = fileHeaders.map(h => ({ original: h, norm: h.toLowerCase().trim() }));

      SYSTEM_FIELDS.forEach(sf => {
        const normKey = sf.key.toLowerCase().replace(/_/g, '');
        const normLabel = sf.label.toLowerCase().replace(/ /g, '');

        let match = normalizedHeaders.find(h =>
          h.norm === normKey || h.norm === normLabel || h.norm.includes(normKey) || (h.norm.includes('uc') && sf.key === 'uc' && !h.norm.includes('novo') && !h.norm.includes('geradora'))
        );

        if (match) initialMap[sf.key] = match.original;
      });
      setColumnMapping(initialMap);
    }
  }, [fileHeaders]);

  const generateMonths = () => {
    const months = [];
    const date = new Date();
    date.setMonth(date.getMonth() - 3); // Start 3 months ago

    const formatter = new Intl.DateTimeFormat('pt-BR', { month: 'short', year: 'numeric' });
    for (let i = 0; i < 15; i++) {
      const parts = formatter.formatToParts(date);
      const m = parts.find(p => p.type === 'month')?.value.toUpperCase() || '';
      const y = parts.find(p => p.type === 'year')?.value || '';
      months.push(`${m}/${y}`);
      date.setMonth(date.getMonth() + 1);
    }
    return months;
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    parseExcel(selectedFile);
  };

  const parseExcel = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];

        // Convert to array of arrays first to get headers
        const jsonData = XLSX.utils.sheet_to_json<any[]>(worksheet, { header: 1 });

        if (jsonData.length > 0) {
          const headers = jsonData[0] as string[];
          // Filter out empty headers and convert to object array
          const validHeaders = headers.filter(Boolean).map(h => String(h).trim());
          setFileHeaders(validHeaders);

          // Convert the rest to object based
          const objectData = XLSX.utils.sheet_to_json(worksheet);
          setFileData(objectData);
          setStep(2);
        } else {
          addToast('A planilha parece estar vazia.', 'error');
        }
      } catch (error) {
        console.error("Error parsing excel", error);
        addToast('Erro ao ler arquivo. Verifique o formato.', 'error');
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files?.length > 0) {
      setFile(e.dataTransfer.files[0]);
      parseExcel(e.dataTransfer.files[0]);
    }
  };

  const handleSubmit = async () => {
    if (!mesReferencia) {
      addToast('Por favor, selecione o mês de referência', 'error');
      return;
    }

    // We only process required fields if there were any, but right now none are strictly required.
    // However, it's good practice to map at least something
    if (Object.keys(columnMapping).length === 0) {
      addToast('Mapeie pelo menos uma coluna (Recomendado: UC)', 'error');
      return;
    }

    setIsProcessing(true);

    try {
      // 1. Create RateioCredito header
      const { data: headerData, error: headerError } = await supabase
        .from('rateios_credito')
        .insert([{
          mes_referencia: mesReferencia.toUpperCase().replace(/\./g, '').replace(/\s/g, ''), // Normalize before insert
          usuario_id: profile?.id,
          usuario_nome: profile?.full_name || 'Desconhecido'
        }])
        .select()
        .single();

      if (headerError) throw headerError;

      const rateioId = headerData.id;

      // 2. Prepare items
      const itemsToInsert = fileData.map(row => {
        return {
          rateio_id: rateioId,
          uc: columnMapping['uc'] ? String(row[columnMapping['uc']] || '') : null,
          outro_numero_uc: columnMapping['outro_numero_uc'] ? String(row[columnMapping['outro_numero_uc']] || '') : null,
          status: columnMapping['status'] ? String(row[columnMapping['status']] || '') : null,
          cpf_cnpj: columnMapping['cpf_cnpj'] ? String(row[columnMapping['cpf_cnpj']] || '') : null,
          nome: columnMapping['nome'] ? String(row[columnMapping['nome']] || '') : null,
          uc_geradora: columnMapping['uc_geradora'] ? String(row[columnMapping['uc_geradora']] || '') : null,
          percent_rateio: columnMapping['percent_rateio']
            ? (val => !isNaN(val) ? val : null)(Number(String(row[columnMapping['percent_rateio']] || '').replace(',', '.')))
            : null,
        };
      });

      // 3. Insert items in batches to avoid payload limits
      const BATCH_SIZE = 500;
      for (let i = 0; i < itemsToInsert.length; i += BATCH_SIZE) {
        const batch = itemsToInsert.slice(i, i + BATCH_SIZE);
        const { error: itemsError } = await supabase.from('rateios_credito_itens').insert(batch);
        if (itemsError) throw itemsError;
      }

      // 4. Sync UG and percent_rateio to unidades_consumidoras
      // O rateio já está salvo: se a sincronização falhar, o upload vale, mas quem subiu
      // precisa saber que o cadastro das UCs ficou como estava (antes isso era só console).
      const { data: sincronizacao, error: rpcError } = await supabase.rpc('sync_uc_from_rateio', { p_rateio_id: rateioId });
      if (rpcError) {
        console.error("Error syncing UCs from rateio:", rpcError);
        addToast(`Rateio salvo, mas o cadastro das UCs não foi atualizado: ${rpcError.message}`, 'warning', 12000);
      } else {
        addToast('Rateio importado com sucesso!', 'success');
      }

      // UC "Pausada" não muda pela planilha: só o "Destravar" de um gestor tira (#6190).
      // Antes da migration 20261014150000 a RPC devolvia void; aí não há lista.
      const pausadas: { numero_uc?: string | null }[] = Array.isArray((sincronizacao as any)?.pausadas_mantidas)
        ? (sincronizacao as any).pausadas_mantidas
        : [];
      if (pausadas.length > 0) {
        const exemplos = pausadas.slice(0, 5).map(p => p.numero_uc).filter(Boolean).join(', ');
        addToast(
          `${pausadas.length} UC(s) continuam "Pausada" no cadastro, mesmo com outro status na planilha` +
            `${exemplos ? ` (${exemplos}${pausadas.length > 5 ? '…' : ''})` : ''}. ` +
            'Um gestor precisa abrir cada uma em Gestão › Saldos das UCs e usar o "Destravar": ' +
            'ela passa a "Reativação" e a próxima planilha acerta a situação.',
          'warning',
          15000
        );
      }

      logUsageEvent({ userId: profile?.id, modulo: 'gestao', funcionalidade: 'rateio_antigo', acao: 'importar_rateio', origem: 'acao' });
      onSuccess();
    } catch (error: any) {
      console.error("Error inserting rateio", error);
      addToast('Erro ao salvar os dados: ' + error.message, 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in custom-scrollbar">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white font-poppins">
            {step === 1 ? 'Novo Rateio de Créditos' : 'Correlacionar Colunas'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 p-6 overflow-y-auto custom-scrollbar">
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Mês Referência do Rateio
                </label>
                <select
                  value={mesReferencia}
                  onChange={(e) => setMesReferencia(e.target.value)}
                  className="w-full bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-xl px-4 py-3 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent transition-all outline-none"
                >
                  <option value="">Selecione o Mês</option>
                  {generateMonths().map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Planilha do Rateio (.xlsx, .csv)
                </label>
                <div
                  className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-8 flex flex-col items-center justify-center text-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/80 transition-colors"
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <UploadCloud className="w-10 h-10 text-primary mb-3" />
                  <p className="text-sm text-gray-900 dark:text-white font-medium">Clique ou arraste a planilha aqui</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Apenas arquivos XLSX/CSV</p>
                </div>
                <input type="file" ref={fileInputRef} className="hidden" accept=".xlsx, .csv" onChange={handleFileUpload} />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <div className="bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300 p-4 rounded-xl flex items-start gap-3">
                <AlertCircle size={20} className="shrink-0 mt-0.5" />
                <p className="text-sm">
                  Detectamos as colunas da sua planilha. Por favor, confirme se de qual coluna da sua planilha vem cada campo do nosso sistema.
                </p>
              </div>

              <div className="space-y-4">
                {SYSTEM_FIELDS.map(field => (
                  <div key={field.key} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-100 dark:border-gray-700">
                    <div className="w-1/2 flex items-center justify-between pr-4">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{field.label}</span>
                      {field.required && <span className="text-[10px] text-red-500 font-bold bg-red-100 dark:bg-red-900/30 px-2 py-0.5 rounded">OBRIGATÓRIO</span>}
                    </div>

                    <div className="w-full sm:w-1/2">
                      <select
                        value={columnMapping[field.key] || ''}
                        onChange={(e) => setColumnMapping(prev => ({ ...prev, [field.key]: e.target.value }))}
                        className="w-full bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                      >
                        <option value="">-- Não importar --</option>
                        {fileHeaders.map(header => (
                          <option key={header} value={header}>{header}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 flex justify-end gap-3">
          {step === 2 && (
            <button
              type="button"
              onClick={() => { setStep(1); setFile(null); }}
              className="px-5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none transition-all flex items-center gap-2"
            >
              Voltar
            </button>
          )}
          <button
            type="button"
            disabled={isProcessing || (step === 1 && (!mesReferencia || !file))}
            onClick={() => {
              if (step === 1 && file && mesReferencia) parseExcel(file);
              else if (step === 2) handleSubmit();
            }}
            className="px-5 py-2.5 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
          >
            {isProcessing ? 'Salvando...' : (step === 1 ? 'Próximo' : 'Salvar Rateio')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default UploadRateioModal;
